<template>
  <div class="staff-dashboard">
    <div class="dashboard-header">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-md-6">
            <h1 class="dashboard-title">{{ $t('staff_dashboard.welcome') }}, {{ staffUser.full_name }}!</h1>
            <p class="dashboard-subtitle">{{ staffUser.role_display }}</p>
            <div v-if="staffUser.department" class="department-info">
              <i class="fas fa-building me-2"></i>
              {{ staffUser.department.name }}
            </div>
          </div>
          <div class="col-md-6 text-right">
            <div class="staff-info">
              <div class="staff-avatar">
                <i class="fas fa-user"></i>
              </div>
              <div class="staff-details">
                <h5>{{ staffUser.full_name }}</h5>
                <p class="text-muted">{{ staffUser.role_display }}</p>
                <button class="btn btn-outline-danger btn-sm" @click="handleLogout">
                  <i class="fas fa-sign-out-alt me-1"></i>
                  {{ $t('staff_dashboard.logout') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="dashboard-content">
      <div class="container">
        <div class="row">
          <!-- Quick Actions -->
          <div class="col-md-4 mb-4">
            <div class="card h-100">
              <div class="card-header">
                <h5 class="mb-0">
                  <i class="fas fa-tasks me-2"></i>
                  {{ $t('staff_dashboard.quick_actions') }}
                </h5>
              </div>
              <div class="card-body">
                <div class="d-grid gap-2">
                  <button 
                    v-for="action in getQuickActions()" 
                    :key="action.id"
                    :class="'btn btn-' + action.variant"
                    @click="handleAction(action.action)"
                  >
                    <i :class="action.icon + ' me-2'"></i>
                    {{ action.label }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Staff Information -->
          <div class="col-md-4 mb-4">
            <div class="card h-100">
              <div class="card-header">
                <h5 class="mb-0">
                  <i class="fas fa-info-circle me-2"></i>
                  {{ $t('staff_dashboard.staff_info') }}
                </h5>
              </div>
              <div class="card-body">
                <div class="row mb-2">
                  <div class="col-5"><strong>{{ $t('staff_dashboard.name') }}:</strong></div>
                  <div class="col-7">{{ staffUser.full_name }}</div>
                </div>
                <div class="row mb-2">
                  <div class="col-5"><strong>{{ $t('staff_dashboard.role') }}:</strong></div>
                  <div class="col-7">
                    <span class="badge bg-primary">{{ staffUser.role_display }}</span>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col-5"><strong>{{ $t('staff_dashboard.email') }}:</strong></div>
                  <div class="col-7">{{ staffUser.email || 'N/A' }}</div>
                </div>
                <div class="row mb-2">
                  <div class="col-5"><strong>{{ $t('staff_dashboard.phone') }}:</strong></div>
                  <div class="col-7">{{ staffUser.phone || 'N/A' }}</div>
                </div>
                <div v-if="staffUser.department" class="row mb-2">
                  <div class="col-5"><strong>{{ $t('staff_dashboard.department') }}:</strong></div>
                  <div class="col-7">{{ staffUser.department.name }}</div>
                </div>
                <div class="row mb-2">
                  <div class="col-5"><strong>{{ $t('staff_dashboard.status') }}:</strong></div>
                  <div class="col-7">
                    <span :class="staffUser.is_active ? 'text-success' : 'text-danger'">
                      {{ staffUser.is_active ? $t('staff_dashboard.active') : $t('staff_dashboard.inactive') }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recent Activity -->
          <div class="col-md-4 mb-4">
            <div class="card h-100">
              <div class="card-header">
                <h5 class="mb-0">
                  <i class="fas fa-clock me-2"></i>
                  {{ $t('staff_dashboard.recent_activity') }}
                </h5>
              </div>
              <div class="card-body">
                <div class="d-flex align-items-center mb-3">
                  <i class="fas fa-sign-in-alt text-success me-3"></i>
                  <div>
                    <p class="mb-0">{{ $t('staff_dashboard.logged_in') }}</p>
                    <small class="text-muted">{{ formatTime(new Date()) }}</small>
                  </div>
                </div>
                <!-- Add more activity items here -->
              </div>
            </div>
          </div>
        </div>

        <!-- Role-specific content -->
        <div class="row mt-4">
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <h5 class="mb-0">
                  <i :class="getRoleIcon() + ' me-2'"></i>
                  {{ getRoleSpecificTitle() }}
                </h5>
              </div>
              <div class="card-body">
                <p>{{ getRoleSpecificDescription() }}</p>
                <!-- Add role-specific functionality here -->
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { removeStorage } from "@/Util/auth";
import NotifyMixin from "@/Util/NotifyMixin";
import { mapGetters } from "vuex";

export default {
  name: "StaffDashboard",
  mixins: [NotifyMixin],
  data() {
    return {
      staffUser: {},
    };
  },
  computed:{
    ...mapGetters(['user'])
  },
  methods: {
    loadStaffProfile() {
      try {
        // Get staff user data from localStorage instead of API
        if (this.user) {
          this.staffUser = this.user;
        } else {
          this.notifyError(this.$t('staff_dashboard.profile_error'));
          this.handleLogout();
        }
      } catch (error) {
        this.notifyError(this.$t('staff_dashboard.profile_error'));
        this.handleLogout();
      }
    },
    handleLogout() {
      removeStorage("auth");
      this.$store.dispatch("user",null);
      if (this.$route.path !== "/") {
        this.$router.push("/");
      } else {
        // If you're already on /, you can reload the page to reset state
        this.$router.go(0);
      }
    },
    getQuickActions() {
      const actions = {
        'housekeeping': [
          { id: 1, label: this.$t('staff_dashboard.actions.room_status'), icon: 'fas fa-bed', variant: 'primary', action: 'room_status' },
          { id: 2, label: this.$t('staff_dashboard.actions.cleaning_schedule'), icon: 'fas fa-calendar', variant: 'info', action: 'cleaning_schedule' },
          { id: 3, label: this.$t('staff_dashboard.actions.supplies'), icon: 'fas fa-box', variant: 'warning', action: 'supplies' },
        ],
        'front desk': [
          { id: 1, label: this.$t('staff_dashboard.actions.check_in'), icon: 'fas fa-sign-in-alt', variant: 'success', action: 'check_in' },
          { id: 2, label: this.$t('staff_dashboard.actions.check_out'), icon: 'fas fa-sign-out-alt', variant: 'danger', action: 'check_out' },
          { id: 3, label: this.$t('staff_dashboard.actions.guest_services'), icon: 'fas fa-concierge-bell', variant: 'info', action: 'guest_services' },
        ],
        'maintenance': [
          { id: 1, label: this.$t('staff_dashboard.actions.work_orders'), icon: 'fas fa-tools', variant: 'primary', action: 'work_orders' },
          { id: 2, label: this.$t('staff_dashboard.actions.equipment'), icon: 'fas fa-cog', variant: 'info', action: 'equipment' },
          { id: 3, label: this.$t('staff_dashboard.actions.repairs'), icon: 'fas fa-wrench', variant: 'warning', action: 'repairs' },
        ],
        'kitchen': [
          { id: 1, label: this.$t('staff_dashboard.actions.menu'), icon: 'fas fa-utensils', variant: 'primary', action: 'menu' },
          { id: 2, label: this.$t('staff_dashboard.actions.orders'), icon: 'fas fa-receipt', variant: 'info', action: 'orders' },
          { id: 3, label: this.$t('staff_dashboard.actions.inventory'), icon: 'fas fa-warehouse', variant: 'warning', action: 'inventory' },
        ],
        'security': [
          { id: 1, label: this.$t('staff_dashboard.actions.patrol'), icon: 'fas fa-shield-alt', variant: 'primary', action: 'patrol' },
          { id: 2, label: this.$t('staff_dashboard.actions.incidents'), icon: 'fas fa-exclamation-triangle', variant: 'danger', action: 'incidents' },
          { id: 3, label: this.$t('staff_dashboard.actions.access_log'), icon: 'fas fa-key', variant: 'info', action: 'access_log' },
        ],
        'concierge': [
          { id: 1, label: this.$t('staff_dashboard.actions.reservations'), icon: 'fas fa-calendar-check', variant: 'primary', action: 'reservations' },
          { id: 2, label: this.$t('staff_dashboard.actions.local_info'), icon: 'fas fa-map-marker-alt', variant: 'info', action: 'local_info' },
          { id: 3, label: this.$t('staff_dashboard.actions.transportation'), icon: 'fas fa-car', variant: 'warning', action: 'transportation' },
        ],
        'management': [
          { id: 1, label: this.$t('staff_dashboard.actions.reports'), icon: 'fas fa-chart-bar', variant: 'primary', action: 'reports' },
          { id: 2, label: this.$t('staff_dashboard.actions.staff'), icon: 'fas fa-users', variant: 'info', action: 'staff' },
          { id: 3, label: this.$t('staff_dashboard.actions.settings'), icon: 'fas fa-cog', variant: 'warning', action: 'settings' },
        ],
        'laundry': [
          { id: 1, label: this.$t('staff_dashboard.actions.laundry_orders'), icon: 'fas fa-tshirt', variant: 'primary', action: 'laundry_orders' },
          { id: 2, label: this.$t('staff_dashboard.actions.inventory'), icon: 'fas fa-warehouse', variant: 'info', action: 'inventory' },
          { id: 3, label: this.$t('staff_dashboard.actions.schedule'), icon: 'fas fa-calendar', variant: 'warning', action: 'schedule' },
        ],
        'room service': [
          { id: 1, label: this.$t('staff_dashboard.actions.orders'), icon: 'fas fa-receipt', variant: 'primary', action: 'orders' },
          { id: 2, label: this.$t('staff_dashboard.actions.menu'), icon: 'fas fa-utensils', variant: 'info', action: 'menu' },
          { id: 3, label: this.$t('staff_dashboard.actions.delivery'), icon: 'fas fa-truck', variant: 'warning', action: 'delivery' },
        ],
      };
      
      return actions[this.staffUser.role] || [];
    },
    getRoleIcon() {
      const icons = {
        'housekeeping': 'fas fa-broom',
        'front desk': 'fas fa-concierge-bell',
        'maintenance': 'fas fa-tools',
        'kitchen': 'fas fa-utensils',
        'security': 'fas fa-shield-alt',
        'concierge': 'fas fa-concierge-bell',
        'management': 'fas fa-user-tie',
        'laundry': 'fas fa-tshirt',
        'room service': 'fas fa-truck',
      };
      return icons[this.staffUser.role] || 'fas fa-user';
    },
    getRoleSpecificTitle() {
      return this.$t(`staff_dashboard.role_titles.${this.staffUser.role}`);
    },
    getRoleSpecificDescription() {
      return this.$t(`staff_dashboard.role_descriptions.${this.staffUser.role}`);
    },
    handleAction() {
      // Handle different actions based on staff role
      this.notifyInfo(this.$t('staff_dashboard.action_coming_soon'));
    },
    formatTime(date) {
      return date.toLocaleTimeString();
    },
    logout() {
      removeStorage("auth");
      this.$store.dispatch("user",null);
      this.$router.push({name:'Login'});
    }
  },
  async mounted() {
    // Check if staff is logged in
    if (!this.user || ['super_admin', 'admin', 'brand_admin', 'account_admin','customer'].includes(this.user.role)) {
      
      this.logout()
      return;
    }
    this.loadStaffProfile();
  },
};
</script>

<style scoped>
.staff-dashboard {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.dashboard-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 30px 0;
}

.dashboard-title {
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 10px;
}

.dashboard-subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
  margin-bottom: 10px;
}

.department-info {
  font-size: 1rem;
  opacity: 0.8;
}

.staff-info {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.staff-avatar {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 24px;
}

.staff-details h5 {
  margin-bottom: 5px;
  color: white;
}

.staff-details p {
  margin-bottom: 10px;
  color: rgba(255, 255, 255, 0.8);
}

.dashboard-content {
  padding: 30px 0;
}

.action-card, .info-card, .activity-card {
  height: 100%;
  border: none;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
}

.action-buttons .btn {
  border-radius: 8px;
  padding: 10px 15px;
  font-weight: 500;
}

.info-list {
  padding: 0;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
}

.info-item:last-child {
  border-bottom: none;
}

.role-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.activity-list {
  padding: 0;
}

.activity-item {
  display: flex;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid #eee;
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-item i {
  width: 30px;
  text-align: center;
  margin-right: 15px;
}

.activity-content p {
  margin-bottom: 5px;
  font-weight: 500;
}

.role-content {
  padding: 20px 0;
}

@media (max-width: 768px) {
  .dashboard-title {
    font-size: 2rem;
  }
  
  .staff-info {
    justify-content: center;
    margin-top: 20px;
  }
}
</style>
